import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomerStatsCardComponent } from '../../../components/customer-stats-card/customer-stats-card.component';
import { DashboardStatData } from '../../../shared/types';
import { MobileDetectionMixin } from '../../../shared/mixins/mobile-detection.mixin';
import {
  DateRange,
  DateRangePickerComponent,
} from '../../../shared/components/date-range-picker/date-range-picker.component';

/**
 * Displays the Revenue tab in the dashboard: shows revenue charts (by service, trend, avg per team), top-selling service, and summary stats while adapting layout for mobile and handling empty states.
 * @component
 * @selector app-revenue-tab
 * @example
 *   <app-revenue-tab
 *     [selectedPeriod]="selectedPeriod"
 *     [periodOptions]="periodOptions"
 *     [selectedLocation]="locationId"
 *     [dashboardStats]="stats"
 *     (selectPeriod)="onPeriodSelect($event)"
 *     (selectLocation)="onLocationSelect($event)">
 *   </app-revenue-tab>
 *
 * Key methods and behavior:
 * - legendItems (getter): derives legend entries (label, color, optional value) from revenueByServiceData for display beside charts.
 * - legendGroups (getter): splits legendItems into sensible groups (single, two columns, or multiple rows of 6) for responsive display.
 * - hasRevenueByServiceData / isRevenueByServiceDataEmpty: determine presence or emptiness of revenue-by-service chart data.
 * - hasRevenueTrendData / isRevenueTrendDataEmpty: determine presence or emptiness of revenue trend chart data.
 * - hasAvgRevenuePerTeamData / isAvgRevenuePerTeamDataEmpty: determine presence or emptiness of average revenue per team chart data.
 * - isAllDataEmpty: utility that checks if all charts and the top-selling service are effectively empty (used for global empty state).
 * - shouldShowEmptyState: logic to decide whether to show a centralized empty state (avoids showing on initial load unless applicable).
 * - shouldShowIndividualEmptyStates: logic to show per-chart empty states when filters have been applied or after initial load.
 * - The component extends a mobile-detection mixin to adjust layout/behavior for small screens; it uses inputs for reactive rendering rather than internal data fetching.
 *
 * Important lifecycle notes:
 * - The component relies on input bindings to update charts and state; no custom lifecycle hooks are defined here (standard Angular change detection applies).
 * - Empty-state logic uses isInitialLoad and hasAppliedFilters to avoid showing empty results on first render unless appropriate.
 *
 * @input {string} selectedPeriod - currently selected reporting period identifier (e.g., "last_30_days").
 * @input {boolean} isDropdownOpen - whether the period dropdown is open.
 * @input {{Array<{value: string, label: string}>}} periodOptions - available period options for selection.
 * @input {string} selectedLocation - currently selected location id.
 * @input {string} selectedLocationLabel - display label for the selected location.
 * @input {boolean} isLocationDropdownOpen - whether the location dropdown is open.
 * @input {{Array<{value: string, label: string}>}} locationOptions - available location options for selection.
 * @input {DashboardStatData[]} dashboardStats - summary statistic cards data shown on the tab.
 * @input {{name: string, unitsSold: number, revenue: number}|null} topSellingService - top selling service summary; null if none.
 * @input {any} revenueByServiceData - chartjs-compatible data object for revenue by service donut/pie chart.
 * @input {any} revenueByServiceOptions - chart options for the revenue-by-service chart.
 * @input {any} revenueTrendData - chartjs-compatible data object for revenue trend (line/area) chart.
 * @input {any} revenueTrendOptions - chart options for the revenue trend chart.
 * @input {any} avgRevenuePerTeamData - chartjs-compatible data object for average revenue per team.
 * @input {any} avgRevenuePerTeamOptions - chart options for the avg revenue per team chart.
 * @input {boolean} isInitialLoad - true when the component is rendering for the first time (used to suppress empty-state UX).
 * @input {boolean} hasAppliedFilters - true when the user has applied filters (affects whether empty states are shown).
 * @input {any} revenueStatsData - additional revenue meta (e.g., { is_revenue_generated: false }) used to determine high-level empty states.
 *
 * @output {EventEmitter<void>} toggleDropdown - emits when the period dropdown toggle is requested.
 * @output {EventEmitter<string>} selectPeriod - emits the selected period id when a period is chosen.
 * @output {EventEmitter<void>} toggleLocationDropdown - emits when the location dropdown toggle is requested.
 * @output {EventEmitter<string>} selectLocation - emits the selected location id when a location is chosen.
 */
@Component({
  selector: 'app-revenue-tab',
  standalone: true,
  imports: [
    CommonModule,
    BaseChartDirective,
    MatTooltipModule,
    CustomerStatsCardComponent,
    DateRangePickerComponent,
  ],
  templateUrl: './revenue-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../dashboard.component.scss',
})
export class RevenueTabComponent extends MobileDetectionMixin {
  @Input() selectedPeriod!: string;
  @Input() selectedPeriodLabel = '';
  @Input() isDropdownOpen = false;
  @Input() periodOptions: { value: string; label: string }[] = [];
  @Input() customDateRange: DateRange = { startDate: null, endDate: null };
  @Input() selectedLocation = '';
  @Input() selectedLocationLabel = '';
  @Input() isLocationDropdownOpen = false;
  @Input() locationOptions: { value: string; label: string }[] = [];
  @Input() dashboardStats: DashboardStatData[] = [];
  @Input() topSellingService: { name: string; unitsSold: number; revenue: number } | null = null;
  @Input() revenueByServiceData: any;
  @Input() revenueByServiceOptions: any;
  @Input() revenueTrendData: any;
  @Input() revenueTrendOptions: any;
  @Input() avgRevenuePerTeamData: any;
  @Input() avgRevenuePerTeamOptions: any;
  @Input() isInitialLoad = true; // Track if this is initial page load
  @Input() hasAppliedFilters = false; // Track if filters have been applied
  @Input() revenueStatsData: any = { is_revenue_generated: false };

  @Output() toggleDropdown = new EventEmitter<void>();
  @Output() selectPeriod = new EventEmitter<string>();
  @Output() dateRangeApply = new EventEmitter<{ period: string; customRange?: DateRange }>();
  @Output() closePeriodDropdown = new EventEmitter<void>();
  @Output() toggleLocationDropdown = new EventEmitter<void>();
  @Output() selectLocation = new EventEmitter<string>();

  // Dynamically derive legend items from the provided chart data
  /**
   * Build an array of legend items from the component's revenueByServiceData labels, dataset backgroundColor and data.
   * @example
   * legendItems()
   * [{ label: 'Service A', color: '#ff0000', value: 1200 }, { label: 'Service B', color: '#00ff00', value: 800 }]
   * @returns {{Array<{ label: string; color: string; value?: number }>} } Returns an array of legend item objects each containing label, color and optional value.
   **/
  get legendItems(): { label: string; color: string; value?: number }[] {
    const labels: string[] = this.revenueByServiceData?.labels ?? [];
    const dataset = this.revenueByServiceData?.datasets?.[0] ?? {};
    const colors: string[] = dataset?.backgroundColor ?? [];
    const values: number[] = dataset?.data ?? [];

    return labels.map((label: string, index: number) => ({
      label,
      color: colors.length ? colors[index % colors.length] : '#cccccc',
      value: values[index],
    }));
  }

  // Create flexible legend groups that adapt to content
  /**
   * Group legend items into display-ready rows: single group when <= 6 items, split into two halves when <= 12, otherwise create groups of up to 6 items each.
   * @example
   * legendGroups()
   * [
   *   [{ label: 'Direct', color: '#4caf50', value: 120 }, { label: 'Referral', color: '#2196f3', value: 80 }, { label: 'Organic', color: '#ff9800', value: 50 }],
   *   [{ label: 'Paid', color: '#f44336', value: 30 }]
   * ]
   * @returns {Array<Array<{ label: string; color: string; value?: number }>>} Array of legend item groups (each group is an array of legend item objects).
   **/
  get legendGroups(): { label: string; color: string; value?: number }[][] {
    const items = this.legendItems;

    // For small number of items, use single group
    if (items.length <= 6) {
      return [items];
    }

    // For medium number of items, split into two groups
    if (items.length <= 12) {
      const midpoint = Math.ceil(items.length / 2);
      return [items.slice(0, midpoint), items.slice(midpoint)];
    }

    // For many items, create multiple groups of 6 items each
    const groups = [];
    for (let i = 0; i < items.length; i += 6) {
      groups.push(items.slice(i, i + 6));
    }
    return groups;
  }

  // Helper method to check if revenue by service data has meaningful values
  hasRevenueByServiceData(): boolean {
    const data = this.revenueByServiceData?.datasets?.[0]?.data;
    return data && data.length > 0 && !data.every((value: number) => value === 0);
  }

  // Helper method to check if revenue by service data is empty or all zeros
  isRevenueByServiceDataEmpty(): boolean {
    const data = this.revenueByServiceData?.datasets?.[0]?.data;
    return !data || data.length === 0 || data.every((value: number) => value === 0);
  }

  // Helper method to check if revenue trend data has meaningful values
  hasRevenueTrendData(): boolean {
    const data = this.revenueTrendData?.datasets?.[0]?.data;
    return data && data.length > 0 && !data.every((value: number) => value === 0);
  }

  // Helper method to check if revenue trend data is empty or all zeros
  isRevenueTrendDataEmpty(): boolean {
    const data = this.revenueTrendData?.datasets?.[0]?.data;
    return !data || data.length === 0 || data.every((value: number) => value === 0);
  }

  // Helper method to check if average revenue per team data has meaningful values
  hasAvgRevenuePerTeamData(): boolean {
    const data = this.avgRevenuePerTeamData?.datasets?.[0]?.data;
    return data && data.length > 0 && !data.every((value: number) => value === 0);
  }

  // Helper method to check if average revenue per team data is empty or all zeros
  isAvgRevenuePerTeamDataEmpty(): boolean {
    const data = this.avgRevenuePerTeamData?.datasets?.[0]?.data;
    return !data || data.length === 0 || data.every((value: number) => value === 0);
  }

  // Helper method to check if ALL chart data is empty (for initial load empty state)
  isAllDataEmpty(): boolean {
    return (
      this.isRevenueByServiceDataEmpty() &&
      this.isRevenueTrendDataEmpty() &&
      this.isAvgRevenuePerTeamDataEmpty() &&
      (!this.topSellingService || this.topSellingService.revenue === 0)
    );
  }

  // Helper method to determine if we should show the Figma empty state
  shouldShowEmptyState(): boolean {
    return !this.isInitialLoad && !this.hasAppliedFilters && this.isAllDataEmpty();
  }

  // Helper method to determine if we should show individual chart empty states
  shouldShowIndividualEmptyStates(): boolean {
    return this.hasAppliedFilters || !this.isInitialLoad;
  }

  trackByValue(_: number, option: { value: string }): string {
    return option.value;
  }
  trackByStatTitle(_: number, stat: DashboardStatData): string {
    return stat.title;
  }
  trackByIndex(index: number): number {
    return index;
  }
  trackByLabel(_: number, item: { label: string }): string {
    return item.label;
  }
}
